/**
 * Calculates main praemie and praemien for all additional objects.
 * Does NOT care about availability, and has no option to exclude something.
 * Use {@link getPraemienInUse} to continue processing if required.
 *
 * @param {{praemien: Object, praemienvermieter: Object}} rawPraemien - raw praemien data (backend data)
 * @param {string} mainproduktKey - calculating praemien for this main product (e.g. "PB")
 * @param {string} produktlinie - calculating praemien for this produktlinie
 * @param {string} haushalt - calculating praemien for this single / family
 * @param {string} beamter - calculating praemien for beamter / not beamter
 * @param {string} sb - calculating praemien for this main selbstbehalt
 * @param {string} cybersb - calculating praemien for this cyber selbstbehalt
 * @param {string} ssrsb - calculating praemien for this ssr selbstbehalt
 * @param {string} laufzeit - calculating praemien for this laufzeit
 * @param {string} zahlweise - calculating praemien for this zahlweise
 * @param {number} maklerRabatt - calculating praemien with this rabatt
 * @param {{id: string, sb: string, bruttojahresmiete: number}[]} vermwohnungen - calculating praemien for these rented locations
 * @param {FzRS: Number, otherProduktKey: number} multiplier - z.B. beim versichern mehrere Fahrzeuge 
 * @returns {{main: number, cyber: number, ssr: number, zweitwohnung: number, vermieter: {id: string, praemie: number}[]}}
 */
export function calculateAllPraemien({
                                       rawPraemien, mainproduktKey, produktlinie, haushalt, beamter, sb, cybersb, ssrsb,
                                       laufzeit, zahlweise, maklerRabatt, vermwohnungen, multiplier, yoligrentner, yoligazubi, yoligekfz, securoFaktor
}) {
  /*console.log("calculateAllPraemien input", {
    rawPraemien, mainproduktKey, produktlinie, haushalt, beamter, sb, cybersb, ssrsb,
    laufzeit, zahlweise, maklerRabatt, vermwohnungen, multiplier, yoligrentner, yoligazubi, yoligekfz
  }); */
  function callCalculateWithDefaults(produkt, sbForCalculation = sb, bruttojahresmiete, multiplierForCalc = multiplier, yoligrentnerForCalc = yoligrentner, yoligazubiForCalc = yoligazubi, yoligekfzForCalc = yoligekfz ) {
    const multiplierNumber = (produkt && multiplierForCalc[produkt]) ? multiplierForCalc[produkt] : 1;
    return calculateSinglePraemie(rawPraemien, produkt, produktlinie, haushalt, beamter, sbForCalculation, laufzeit, zahlweise, maklerRabatt, bruttojahresmiete, multiplierNumber, yoligrentnerForCalc, yoligazubiForCalc, yoligekfzForCalc, securoFaktor);
  }
  function callCalculateWithoutAzubi(produkt, sbForCalculation = sb, bruttojahresmiete, multiplierForCalc = multiplier, yoligrentnerForCalc = yoligrentner, yoligazubiForCalc = 0, yoligekfzForCalc = yoligekfz ) {
    const multiplierNumber = (produkt && multiplierForCalc[produkt]) ? multiplierForCalc[produkt] : 1;
    return calculateSinglePraemie(rawPraemien, produkt, produktlinie, haushalt, beamter, sbForCalculation, laufzeit, zahlweise, maklerRabatt, bruttojahresmiete, multiplierNumber, yoligrentnerForCalc, yoligazubiForCalc, yoligekfzForCalc, securoFaktor);
  }

  const main = callCalculateWithDefaults(mainproduktKey);
  const mainnoazubi =  yoligazubi ? callCalculateWithoutAzubi(mainproduktKey) : main;
  const cyber = callCalculateWithDefaults("Cyber", cybersb);
  const ssr = callCalculateWithDefaults("SSR", ssrsb);
  const zweitwohnung = callCalculateWithDefaults("zus. HWRS");
  const vermieter = vermwohnungen.map(wohnung => ({
    id: wohnung.id,
    praemie: callCalculateWithDefaults("VermRS", wohnung.sb, wohnung.bruttojahresmiete)
  }));
 /*console.log("calculateAllPraemien output", {
    main,
    cyber,
    ssr,
    zweitwohnung,
    vermieter,
    mainnoazubi
  }); */
  return {
    main,
    cyber,
    ssr,
    zweitwohnung,
    vermieter,
    mainnoazubi
  };
}

/**
 * Filters given calculated praemien data ({@link calculateAllPraemien}), and calculates total praemie.
 *
 * @param {{main: number, cyber: number, ssr: number, zweitwohnung: number, vermieter: {id: string, praemie: number}[]}} calculatedPraemien - precalculated praemien, {@link calculateAllPraemien}
 * @param {boolean} useSsr - decides if "ssr" is included in the result
 * @param {boolean} useCyber - decides if "cyber" is included in the result
 * @param {boolean} useZweitwohnung - decides if "zweitwohnung" is included in the result
 * @param {boolean} useVermieter - decides if "vermieter" is included in the result
 * @returns {{main: number, cyber: number, ssr: number, zweitwohnung: number, vermieter: {id: string, praemie: number}[], total: number}} results, entries are set to 0.0 or [] if they are not selected
 */
export function getPraemienInUse(calculatedPraemien, { useSsr, useCyber, useZweitwohnung, useVermieter }) {

  const praemienWithoutUnused = {
    main: calculatedPraemien.main,
    mainnoazubi: calculatedPraemien.mainnoazubi,
    cyber: useCyber ? calculatedPraemien.cyber : 0.0,
    ssr: useSsr ? calculatedPraemien.ssr : 0.0,
    zweitwohnung: useZweitwohnung ? calculatedPraemien.zweitwohnung : 0.0,
    vermieter: useVermieter ? calculatedPraemien.vermieter : []
  };
  praemienWithoutUnused.total = preciseRound(
    praemienWithoutUnused.main +
    praemienWithoutUnused.cyber +
    praemienWithoutUnused.ssr +
    praemienWithoutUnused.zweitwohnung +
    praemienWithoutUnused.vermieter.reduce((vermieterPraemie, wohnung) => vermieterPraemie + wohnung.praemie, 0.0));

  return praemienWithoutUnused;
}

/**
 * Calculates praemie for given data.
 *
 * @param {{praemien: Object, praemienvermieter: Object}} rawPraemien - raw praemien data (backend data)
 * @param {string} produkt - calculating praemie for this produkt
 * @param {string} produktlinie - calculating praemie for this produktlinie
 * @param {string} haushalt - calculating praemie for this single / family
 * @param {string} beamter - calculating praemie for beamter / not beamter
 * @param {string} sb - calculating praemie for this selbstbehalt
 * @param {string} laufzeit - calculating praemie for this laufzeit
 * @param {string} zahlweise - calculating praemie for this zahlweise
 * @param {number} [maklerRabatt=0.0] - calculating praemie with this rabatt
 * @param {number} [bruttojahresmiete] - Used only if produkt is Vermieter RS
 * @returns {number} calculated praemie
 */
export function calculateSinglePraemie(rawPraemien, produkt, produktlinie, haushalt, beamter, sb, laufzeit, zahlweise, maklerRabatt = 0.0, bruttojahresmiete, praemienMultiplier = 1, yoligrentner = false, yoligazubi = false, yoligekfz = false, securoFaktor = 1) {

  const jahresPraemie = getBasePraemieFromRaw(rawPraemien, produkt, produktlinie, haushalt, beamter, sb, bruttojahresmiete, yoligrentner);
  const yoligazubiRabatt = getAzubiRabatt(produkt, produktlinie, yoligazubi);
  const yoligekfzRabatt = (yoligekfz && produktlinie === 'YOLIG' && /^P.*V/.test(produkt) ) ? 5.0 : 0.0;
  if (jahresPraemie === 0) {
    return 0;
  } else {
    //Depending on the aktual DMB calculation the praemienMultiplier must be 
    //applied to jahresPraemie or  the result of adjustPraemie
    return adjustPraemie(jahresPraemie * praemienMultiplier, zahlweise, laufzeit, maklerRabatt, yoligazubiRabatt , yoligekfzRabatt, securoFaktor);
  }
}

function getAzubiRabatt(produkt, produktlinie, yoligazubi) {
  if (!yoligazubi) return 0.0;
  if (produktlinie != 'YOLIG') return 0.0;
  switch (produkt) {
    case "PBVW": return 23.0;
    case "PBV": return 24.5;
    case "PVW": return 16.0;
    case "PBW": return 30.0;
    case "PB": return 29.0;
    default: return 0.0;
  }
}

/**
 * Looks for desired praemie in given raw data
 *
 * @param {{praemien: Object, praemienvermieter: Object}} rawPraemien - raw praemien data (backend data)
 * @param {string} produkt - looking for praemie for this produkt
 * @param {string} produktlinie - looking for praemie for this produktlinie
 * @param {string} haushalt - looking for praemie for this single / family
 * @param {string} beamter - looking for praemie for beamter / not beamter
 * @param {string} sb - looking for praemie for this selbstbehalt
 * @param {number} [bruttojahresmiete] - Used only if produkt is Vermieter RS
 * @returns {number} base praemie for given input values
 */
export function getBasePraemieFromRaw(rawPraemien, produkt, produktlinie, haushalt, beamter, sb, bruttojahresmiete, yoligrentner) {
  if (containsNullOrEmptyString([produktlinie, produkt, sb])) {
    return 0;
  }

  if (produkt === "VermRS" && !bruttojahresmiete) {
    return 0;
  }

  // EXP Praemie is returned for some Produkts if no PRE Praemie is defined
  if (['Cyber', 'Fahrer', 'V-Familie', 'FzRS', 'V-VN'].includes(produkt) && produktlinie === "PRE") {
    produktlinie = "EXP";
  }

// EXP and YOLIG Praemie is NOT the same for Zusatz-RS and Pooltarif anymore 'VermRS', 'SSR', 'zus. HWRS' have YOLIG
  if (['Cyber'].includes(produkt) && produktlinie === "YOLIG") {
    produktlinie = "EXP";
  }

  if (produkt === "S&S PBVW+SSR") {
    // schnell und sicher is only available for specific values; some of them are forced on the ui
    // for haushalt and beamter, ui allows different settings to collect data; we still want to get the S&S praemie.
    // TODO: is this ok? S&S available for EPH and beamter?
    //haushalt = "MPH"; // restction on Haushalt removed with ARB 2020
    beamter = "N";
  }

  const path = getRawPraemienPath(produkt, produktlinie, haushalt, beamter, sb, yoligrentner);

  return getJahresPraemie(rawPraemien, path, produkt, bruttojahresmiete);
}

/**
 * Adjusts given jahresPraemie according to zahlweise, laufzeit and maklerRabatt.
 *
 * @param {number} jahresPraemie - jahres praemie to adjust
 * @param {string} zahlweise - selected zahlweise
 * @param {string} laufzeit - selected laufzeit (differentation between "3" and others)
 * @param {number} maklerRabatt - rabatt; x = x% rabatt; only for Privatrechner!
 * @returns {number} adjusted praemie
 */
export function adjustPraemie(jahresPraemie, zahlweise, laufzeit, maklerRabatt = 0.0, yoligazubiRabatt = 0.0, yoligekfzRabatt = 0.0 , securoFaktor = 1.0) {

  const laufzeitFaktor = (laufzeit === "3") ? 0.95 : 1.0;
  let zahlweiseFaktor = 1.0;
  if (zahlweise === "1") {
    zahlweiseFaktor = 1.0;
  }
  else if (zahlweise === "2") {
    zahlweiseFaktor = 1.03;
  }
  else if (zahlweise === "4") {
    zahlweiseFaktor = 1.05;
  }
  else if (zahlweise === "12") {
    zahlweiseFaktor = 1.06;
  }

  const maklerFaktor = 1.0 - (maklerRabatt / 100);
  const azubiFaktor = 1.0 - (yoligazubiRabatt / 100);
  const ekfzFaktor = 1.0 - (yoligekfzRabatt / 100);

  // Studenten-Nachlass E-Fahrzeug Powermakler Laufzeitrabatt Ratenzahlungszuschlag

  let rabattPraemie = preciseRound(jahresPraemie * azubiFaktor);
  rabattPraemie = preciseRound(rabattPraemie * ekfzFaktor);
  rabattPraemie = preciseRound(rabattPraemie * maklerFaktor);
  rabattPraemie = preciseRound(rabattPraemie * laufzeitFaktor);
  rabattPraemie = preciseRound(rabattPraemie * zahlweiseFaktor);
  rabattPraemie = preciseRound(rabattPraemie * securoFaktor);

  const rate = preciseRound(rabattPraemie / zahlweise);
  // bis 2022-08
  //const rabattPraemieOld = preciseRound(jahresPraemie * laufzeitFaktor) * maklerFaktor * azubiFaktor * ekfzFaktor;
  //const basisRateOld = preciseRound(rabattPraemieOld / zahlweise);
  //const rateOld = preciseRound(basisRateOld * zahlweiseFaktor);
  //if (rate != rateOld) {
  // log Calculation Differences console.log('old', rateOld, 'new', rate);
  //}
  return rate;
}

/**
 * Combine categories to string as used in Praemien calculation (incl correct order).
 *
 * @param {string[]} categories - the categories to combine. Entries should only be: "P,"B","V" or "W"
 * @returns {string} categories combined in correct order, empty string if combination is invalid.
 */
export function productKeyFromCategories(categories) {
  let key = "";
  if (categories.includes("P")) key += "P";
  if (categories.includes("B")) key += "B";
  if (categories.includes("V")) key += "V";
  if (categories.includes("W")) key += "W";
  if (categories.includes("M")) key += "M";

  if (!key.match(/\bPBVW\b|\bPBW\b|\bPBV\b|\bPVW\b|\bPB\b|\bPV\b|\bPW\b|\bP\b|\bV\b|\bM\b/)) {
    // invalid combination
    return "";
  } else {
    return key;
  }
}


/** ***** INTERNAL HELPERS FROM HERE ON ****** **/

function containsNullOrEmptyString(array) {
  return array.some(item => item === null || item === "");
}

/**
 * Generates path as array where the praemie can be found in raw data. Combines given values to an array to be used
 * in {@link getJahresPraemie}.
 *
 * @return {array} array containing some or all of the input params in correct order for lookup in raw praemien data
 */
function getRawPraemienPath(produkt, produktlinie, haushalt, beamter, sb, yoligrentner) {
  if (produkt === "VermRS") {
    // Vermieter RS has "beamter" and "sb" in different order than other products
    return [produkt, produktlinie, sb, beamter];
  }
  if (produktlinie === "YOLIG" && yoligrentner && produkt.indexOf("PB") === 0) {
    return [produkt, "YOLIR", haushalt, beamter, sb];
  }
  const path = [produkt, produktlinie, haushalt, beamter, sb];
  const bausteineWithoutHaushalt = ['Cyber', 'SSR', 'VermRS', 'zus. HWRS', 'Fahrer', 'V-Familie', 'FzRS', 'V-VN', 'M'];
  if (bausteineWithoutHaushalt.includes(produkt)) {
    path.splice(2, 1);
  }
  return path;
}

/**
 * Get requested praemie from rawPraemien. Special handling for Vermieter RS.
 *
 * @param {{praemien: Object, praemienvermieter: Object}} rawPraemien - raw praemien data
 * @param {array} path - the path to lookup in rawPraemien data
 * @param {string}produkt - the target produkt, only used for differentiation of Vermieter RS
 * @param {number} [bruttojahresmiete] - jahresmiete, only used for Vermieter RS
 * @returns {number} requested jahresPraemie
 */
function getJahresPraemie(rawPraemien, path, produkt, bruttojahresmiete) {
  let jahresPraemie;

  if (produkt === "VermRS") {
    const rawRecords = traverseObject(rawPraemien.praemienvermieter, path);
    if (rawRecords === undefined) {
      return 0;
    }
    const record = rawRecords.find(record => bruttojahresmiete > record.von && bruttojahresmiete <= record.bis);
    if (record === undefined) {
      return 0;
    }
    if (record.minimum === 0) {
      jahresPraemie = bruttojahresmiete * record.promille / 1000;
    } else {
      jahresPraemie = record.minimum;
    }
  } else {
    const rawPraemie = traverseObject(rawPraemien.praemien, path);
    if (!rawPraemie || !rawPraemie.p1) {
      jahresPraemie = 0;
    } else {
      jahresPraemie = rawPraemie.p1;
    }
  }

  return jahresPraemie;
}

/**
 * Traverses a nested object. Basically the same as:
 * object[path[0]][path[1]][path[2]]...
 * But does not throw an error if undefined is accessed. Returns undefined in this case.
 *
 * @param {Object} object - the object to traverse
 * @param {array} path - the path to traverse
 * @returns {*} result found at given path
 */
function traverseObject(object, path) {
  if (!object) {
    return undefined;
  }
  let result = object;
  for (const step of path) {
    if (result[step]) {
      result = result[step];
    } else {
      // uncomment to check failed access to raw praemien data. This can be part of normal processing!
      // console.log("Failed to traverse object. Path: " + JSON.stringify(path));
      // console.log("Step: " + step);
      return undefined;
    }
  }
  return result;
}

/**
 * Circumvents problems in Number.toFixed because of floating point precision.
 * e.g. 2.55.toFixed(1) returns '2.5' instead of 2.6; also, toFixed returns a string, we want number
 *
 * @param {number} number - the number to round
 * @param {number} [precision=2] - the target number of digits after decimal separator
 * @returns {number} rounded number
 */
export function preciseRound(number, precision = 2) {
  const factor = Math.pow(10, precision);
  return (Math.floor((number * factor) + 0.50001)) / factor;
}

